import { Flex } from "antd"
import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { Outlet, useNavigate, useParams } from "react-router-dom"

import { generateDocumentTitle } from "../../../../constants/document-title.ts"
import { RoutesPaths } from "../../../../constants/routes.ts"
import type { RootState } from "../../../../store.ts"
import {
  selectActiveBranchId,
  selectIsCompanyMode,
  selectPageFilter
} from "../../../Branches/redux/branches.slice.ts"
import { PageBranchFilter } from "../../../Branches/components/PageBranchFilter.tsx"
import { reportsSetFilters } from "../../redux/reports.slice.ts"
import { fetchReports, toggleReportFavorite } from "../../redux/reports.thunks.ts"
import type { ReportListItem } from "../../types/types.ts"
import { ReportsRail, type ReportsNav } from "./ReportsRail.tsx"

export interface ReportsShellOutletContext {
  activeNav: ReportsNav
}

const ReportsShell = ({
  results,
  fetching,
  filters,
  isCompanyMode,
  activeBranchId,
  reportsPageScopeKey,
  setFilters,
  getReports,
  toggleFavorite
}: ConnectedProps<typeof connector>) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id: selectedId } = useParams<{ id: string }>()
  const [activeNav, setActiveNav] = useState<ReportsNav>("all")

  useEffect(() => {
    generateDocumentTitle(t("reports.title"))
  }, [t])

  useEffect(() => {
    getReports(filters)
    // The branch scope (global workspace branch, or Company HQ's own
    // saved Reports filter) lives inside the thunk itself
    // (`reportsBranchScopeParams`) — re-run whenever either changes.
  }, [getReports, filters, isCompanyMode, activeBranchId, reportsPageScopeKey])

  const handleSearch = useCallback(
    (search: string | null) => setFilters({ search }),
    [setFilters]
  )

  const handleNavChange = useCallback(
    (nav: ReportsNav) => {
      setActiveNav(nav)
      navigate(RoutesPaths.reports.list)
    },
    [navigate]
  )

  const handleSelect = useCallback(
    (report: ReportListItem) => navigate(RoutesPaths.reports.view(report.id)),
    [navigate]
  )

  const handleCreate = useCallback(
    () => navigate(RoutesPaths.reports.create),
    [navigate]
  )

  const handleToggleFavorite = useCallback(
    (report: ReportListItem) =>
      toggleFavorite({
        id: report.id,
        previousIsFavorite: report.is_favorite
      }),
    [toggleFavorite]
  )

  const context: ReportsShellOutletContext = { activeNav }

  return (
    // Grid's default `auto` row tracks share the container's full `size-full`
    // height via `align-content: normal` (a `stretch` behavior for auto
    // tracks) — with no explicit sizing, that handed the branch-filter row
    // the same share of free space as the content row below it, leaving the
    // filter correctly at the top but the rail/content starting hundreds of
    // pixels down. `auto` + `1fr` pins the filter row to its own content and
    // gives every remaining pixel to the row that actually needs it.
    <div className="grid grid-rows-[auto_1fr] size-full">
      <div className="px-1 pt-1">
        <PageBranchFilter page="reports" />
      </div>
      <Flex
        gap="large"
        className="min-h-0 max-h-[calc(100vh-88px)] overflow-hidden p-1 -m-1"
      >
        <ReportsRail
          reports={results}
          loading={fetching}
          activeNav={activeNav}
          selectedId={selectedId}
          search={filters.search || ""}
          onNavChange={handleNavChange}
          onSearch={handleSearch}
          onSelect={handleSelect}
          onCreate={handleCreate}
          onToggleFavorite={handleToggleFavorite}
        />
        <div className="min-w-0 flex-1 overflow-auto">
          <Outlet context={context} />
        </div>
      </Flex>
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  results: state.reports.results,
  fetching: state.reports.fetching,
  filters: state.reports.filters,
  isCompanyMode: selectIsCompanyMode(state),
  activeBranchId: selectActiveBranchId(state),
  reportsPageScopeKey: JSON.stringify(selectPageFilter("reports")(state))
})

const mapDispatchToProps = {
  setFilters: reportsSetFilters,
  getReports: fetchReports,
  toggleFavorite: toggleReportFavorite
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ReportsShell)
